import React from 'react';
import {Rankings} from '../../types/rankings';
import {
  styled,
  TextTag,
  Text,
  TokenTextAppearance,
  CTA,
  Breakpoints,
} from '@volkswagen-onehub/components-core';
import useTrans from '../../hooks/use-trans';
import {useFeatureServices} from '../../use-feature-services';
import {Stat} from '../../types/stats';
import {ContainerMain} from '../../styles/global.style';

interface ResultRecommendationInterface {
  rankings: Rankings;
}

const ResultRecommendationContainer = styled.div`
  margin-bottom: ${(props) => props.theme.size.dynamic0140};
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.dynamic0130};
  align-items: center;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.grid001};

  a {
    font-size: 1.3rem;
  }

  @media screen and (min-width: 768px) {
    flex-direction: row;
  }
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.size.dynamic0040};

  img {
    width: 100%;
    height: 100%;
    border-radius: ${(props) => props.theme.size.borderRadius.imageL};
  }

  a {
    font-weight: bold;
  }
`;

const ImageLink = styled.a`
  display: block;
  cursor: pointer;
`;

const CardCTAWrapper = styled.div`
  padding-left: ${(props) => props.theme.size.grid001};

  @media screen and (min-width: 768px) {
    padding-left: 0;
  }
`;

const SuggestionTitle = styled.div`
  width: 100%;
  padding: 0 ${(props) => props.theme.size.grid001};
  text-align: center;

  @media screen and (min-width: ${Breakpoints.b960}px) {
    padding: 0;
  }
`;

const ResultRecommendation: React.FC<ResultRecommendationInterface> = ({
  rankings,
}) => {
  const {tracking} = useFeatureServices();
  const trans = useTrans();

  const handleLearnClick = (
    e: React.SyntheticEvent<HTMLAnchorElement>,
    stat: Stat,
  ) => {
    e.preventDefault();

    tracking.track('VWBasic_Link_Click', {
      eventInfo: {
        contentId: stat.result?.carlineName,
        brand: "passenger"
      },
      configuration: {
        CarlineName: stat.result?.carlineName,
      },
      process: {
        Name: 'SUVW Questionnaire and Lead Capture Form',
        StepName: 'Results',
      },
      link: {
        url: stat.result?.cta.learn.url,
        name: stat.result?.cta.learn.linkName,
      },
    });

    window.open(stat.result?.cta.learn.url, '_self');
  };

  return (
    <ContainerMain>
      <ResultRecommendationContainer>
        <SuggestionTitle>
          <Text
            tag={TextTag.h3}
            appearance={TokenTextAppearance.headline400}
            bold
          >
            {trans.pages.results.suggestion}
          </Text>
        </SuggestionTitle>
        <CardsContainer>
          {rankings.slice(1, 4).map(([key, stat]) => (
            <CardContainer key={key}>
              <ImageLink
                href={stat.result?.cta.learn.url}
                onClick={(e) => handleLearnClick(e, stat)}
              >
                <img
                  src={stat.result?.imageRecommended.url}
                  alt={stat.result?.imageRecommended.alt}
                />
              </ImageLink>

              <CardCTAWrapper>
                <CTA
                  tag="a"
                  emphasis="tertiary"
                  href={stat.result?.cta.learn.url}
                  onClick={(e) => {
                    handleLearnClick(e, stat);
                  }}
                >
                  {stat.model.modelName}
                </CTA>
              </CardCTAWrapper>
            </CardContainer>
          ))}
        </CardsContainer>
      </ResultRecommendationContainer>
    </ContainerMain>
  );
};

export default ResultRecommendation;
